import { debounce } from '@solid-primitives/scheduled'
import type { RecordingWithHost } from '@state/recordings/api'
import { createEffect, createSignal } from 'solid-js'

export type RecordingPeriod = 'any' | '30d' | '90d' | '1y'
export type RecordingSort = 'newest' | 'oldest'

export type RecordingFilters = {
	search: string
	/** `${source_type}:${filter_value}` of a discovery row, or null for every group */
	group: string | null
	period: RecordingPeriod
	evergreenOnly: boolean
	sort: RecordingSort
}

export const recordingFilterDefaults: RecordingFilters = {
	search: '',
	group: null,
	period: 'any',
	evergreenOnly: false,
	sort: 'newest',
}

export function useState() {
	const [editRecording, setEditRecording] = createSignal<
		RecordingWithHost | undefined
	>()
	const [isCreateFormOpen, setIsCreateFormOpen] = createSignal(false)

	const [filters, setFilters] = createSignal<RecordingFilters>(recordingFilterDefaults)
	const [searchInput, setSearchInputRaw] = createSignal('')
	const commitSearch = debounce((search: string) => setFilters({ ...filters(), search: search.trim() }), 300)

	const setSearchInput = (value: string) => {
		setSearchInputRaw(value)
		commitSearch(value)
	}
	/** Set the search text without debouncing (e.g. from the URL) */
	const setSearch = (search: string) => {
		commitSearch.clear()
		setSearchInputRaw(search)
		setFilters({ ...filters(), search: search.trim() })
	}
	const patchFilters = (patch: Partial<RecordingFilters>) => setFilters({ ...filters(), ...patch })
	const clearFilters = () => {
		commitSearch.clear()
		setSearchInputRaw('')
		setFilters(recordingFilterDefaults)
	}

	/** Filters other than the search text that differ from the defaults */
	const activeFilterCount = () => {
		const f = filters()
		return [
			f.group !== null,
			f.period !== 'any',
			f.evergreenOnly,
			f.sort !== 'newest',
		].filter(Boolean).length
	}
	/** Search or filters are narrowing the page, so show one flat result list instead of the groups */
	const isFiltering = () => !!filters().search || activeFilterCount() > 0

	createEffect(() => {
		if (isCreateFormOpen() === false) {
			setEditRecording(undefined)
		}
	})

	createEffect(() => {
		if (editRecording()) {
			setIsCreateFormOpen(true)
		}
	})

	return {
		editRecording,
		setEditRecording,
		isCreateFormOpen,
		setIsCreateFormOpen,
		filters,
		patchFilters,
		clearFilters,
		searchInput,
		setSearchInput,
		setSearch,
		activeFilterCount,
		isFiltering,
	}
}
